import { Upload, message } from 'antd';
import Tooltip from '../common/Tooltip';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { convertContentToRawText, decorator, handleBeforeInput } from '../RichEditor/utils';
import { transformTextToEditorState } from '../../utils/dataSync';
import { UploadOutlined } from '@ant-design/icons';
import { ContentState, Editor, EditorState, convertToRaw } from 'draft-js';

import { uploadDocumentMedia } from '../../requests/mediaUpload';
import { MAX_MEDIA_SIZE, MAX_MEDIA_SIZE_LABEL } from '../../shared/media/mediaLimits';
import { hideUploadProgress, showUploadProgress } from '../../utils/uploadProgress';
import { pageWidth } from '../../constants/gridConfig';
import { maxContainerHeight } from '../../utils/containers';
import ReactDOM from 'react-dom';
import { MENU_ANCESTORS, PLANNING_SCREENSHOT_TABLES, viewerTypes } from '../../constants/constants';
import { useStore } from '../../store/store';
import Loader from '../../Loader';
import PlanningCaptureUnavailable from '../common/PlanningCaptureUnavailable';
import { planningStatusOfBox } from '../../utils/planningScreenshots';
import { convertPadding, getCellStyle } from '../../utils/table';
import ColumnResizer from './ColumnResizer';
import FloatyTextStyling from './FloatyTextStyling';
import { useCellClickOutside } from './useCellClickOutside';
import { useInsertionTargetRegistration } from './insertionTarget';
import EditorWrapper from '../common/EditorWrapper';
import ContextualMenu from '../ContextualMenu/ContextualMenu';
import { EditorProvider } from '../../contexts/EditorContext';
import ControlRow from './ControlRow';
import { usePreviewContext } from '../../contexts/PreviewContext';
import { useTabHandler } from '../../hooks';
import { RichUtils } from 'draft-js';
import '../RichEditor/index.css'
import { useStyledPaste } from '../RichEditor/useStyledPaste';
import { mergeConfigurationHighlightColors } from '../../utils/styles';

// Valeurs flex reçues (cf. Table.convertVerticalAlign) → vertical-align CSS.
const TD_VERTICAL_ALIGN = { 'flex-start': 'top', center: 'middle', 'flex-end': 'bottom' };

const Cell = ({
  rowSpan,
  colSpan = 1,
  isCellSelected,
  rowIndex,
  colIndex,
  hidden,
  handleClickInTable,
  type,
  cellContent,
  box,
  tableIsSelected,
  boxId,
  columnKey,
  rowKey,
  theadHeight,
  tableStyle,
  align,
  verticalAlign,
  isEven,
  rotation,
  setColumnSizeVars,
  nextColKey,
  computeMaxColumnWidth,
  isLastColumn,
  drawMode,
  isFirstRow,
  isLastRow,
  isFirstColumn,
  controlRowsClicked,
  setControlRowsClicked,
  nextRowKey,
  zIndex,
  placementIndex,
  groupRowSpan = 1,
  columnLength,
  moveRow,
  onControlRowClick,
  configuration,
  currentDocument,
  currentProposal,
  landscape,
  header,
  footer,
  updateBox,
  setEventManagerEnabled,
  fromPdf,
  styleMap,
  rowStyle
}) => {
  const { fromViewer } = usePreviewContext();

  // Cellule image d'un tableau planning-screenshot : si vide, le screenshot est
  // en cours de génération (async) -> loader Smart4 au lieu du placeholder upload.
  const isPlanningScreenshotCell =
    type === 'image' &&
    PLANNING_SCREENSHOT_TABLES.includes(box?.tableName?.toUpperCase());
  // Vide et en échec : « Capture indisponible » au lieu d'un loader sans fin.
  const planningCaptureFailed = useStore(
    (state) =>
      isPlanningScreenshotCell &&
      planningStatusOfBox(state.planningScreenshotStatus, box, state.currentDocument?.id) === 'failed'
  );

  const [editorState, setEditorState] = useState(transformTextToEditorState(cellContent));
  const [isSelected, setIsSelected] = useState(false)
  const [defaultSelection, setDefaultSelection] = useState()
  useInsertionTargetRegistration({
    boxId,
    isSelected: tableIsSelected && isSelected,
    wantsPanel: type !== 'image',
    editorState,
    setEditorState,
    setDefaultSelection,
  });
  const consumeSkipBoxSaveOnUnselect = useStore((state) => state.consumeSkipBoxSaveOnUnselect);
  const documentContentSyncTick = useStore((state) => state.documentContentSyncTick);


  useEffect(() => {
    if (!isSelected) {
      setEditorState(transformTextToEditorState(cellContent))
    }
  }, [cellContent, isSelected]);

  useEffect(() => {
    setEditorState(transformTextToEditorState(cellContent));
  }, [documentContentSyncTick, cellContent]);

  const inputRef = useRef()
  const parentRowType = "cell"
  const clickedRow = controlRowsClicked.find(row => row.parentRowId === rowKey && row.parentRowType === parentRowType)
  const isControlRowClicked = !!clickedRow
  const afterClickedRow = nextRowKey ? controlRowsClicked.find(row => row.parentRowId === nextRowKey && row.parentRowType === parentRowType) : null
  const isControlRowAfterClicked = !!afterClickedRow
  const isControlRowClickedToDelete = isControlRowClicked && clickedRow?.isToDelete

  const columnClickedRow = controlRowsClicked.find(row => row.parentRowId === columnKey && row.parentRowType === "header")
  const isColumnControlRowClicked = !!columnClickedRow
  const isColumnControlRowClickedToDelete = isColumnControlRowClicked && columnClickedRow?.isToDelete

  // Style de la ligne (configuration du tableau) par-dessus celui du tableau :
  // le bloc de texte en reprend la couleur.
  const style = {
    ...getCellStyle({
      hidden,
      tableStyle,
      isEven,
      configuration,
      columnKey,
      isLastColumn,
    }),
    ...rowStyle,
  }

  // Fonction helper pour propager une valeur aux lignes en dessous qui ont columnKey dans hiddenColumnsKeys
  const propagateValueToHiddenRows = useCallback((box, rowIndex, columnKey, value) => {
    for (let i = rowIndex + 1; i < box.content.data.length; i++) {
      const nextRow = box.content.data[i];
      if (nextRow.hiddenColumnsKeys && nextRow.hiddenColumnsKeys.includes(columnKey)) {
        nextRow[columnKey] = value;
      } else {
        // Arrêter dès qu'on trouve une ligne sans columnKey dans hiddenColumnsKeys
        break;
      }
    }
  }, []);

  /* Une cellule ne retient que l'URL : le palier est donc figé à l'insertion
     (`pdf`, le contexte du document) et n'est pas modifiable après coup. */
  const handleUploadImg = useCallback(async (file) => {
    if (file.file.size > MAX_MEDIA_SIZE) {
      message.error({
        content: `La taille du fichier ne doit pas dépasser ${MAX_MEDIA_SIZE_LABEL}`,
        key: 'file-too-large',
      });
      return;
    }

    const documentId = currentDocument.type === 'template'
      ? currentDocument.id
      : currentProposal.id;

    try {
      showUploadProgress(0);
      const { selection } = await uploadDocumentMedia({
        documentId,
        documentType: currentDocument.type,
        file: file.file,
        onProgress: showUploadProgress,
      });

      const newEditorState = EditorState.createWithContent(
        ContentState.createFromText(selection.url),
        decorator
      )
      updateBox(boxId, (box) => {
        box.content.data.find((row) => row.key === rowKey)[columnKey] = JSON.stringify(
          convertToRaw(newEditorState.getCurrentContent()));
      });
      setEditorState(newEditorState)
    } catch (err) {
      message.error({
        content: "Erreur lors de l'upload de l'image",
        key: 'upload-error',
      });
    } finally {
      hideUploadProgress();
    }
  }, [boxId, columnKey, currentDocument.id, currentDocument.type, currentProposal.id, rowKey, updateBox]);

  const onChangeCellText = useCallback(
    async (value) => {
      if (type === 'image') {
        if (!value) {
          const emptyEditorState = EditorState.createEmpty(decorator)
          updateBox(boxId, (box) => {
            const rowIndex = box.content.data.findIndex((row) => row.key === rowKey)
            const row = rowIndex !== -1 ? box.content.data[rowIndex] : null
            if (row) {
              const newValue = JSON.stringify(
                convertToRaw(emptyEditorState.getCurrentContent()));
              row[columnKey] = newValue;
              propagateValueToHiddenRows(box, rowIndex, columnKey, newValue);
            }
          });
          setEditorState(emptyEditorState)
          return;
        }
        handleUploadImg(value)
        return;
      }
      updateBox(boxId, (box) => {
        const rowIndex = box.content.data.findIndex((row) => row.key === rowKey)
        const row = rowIndex !== -1 ? box.content.data[rowIndex] : null
        if (row) {
          row[columnKey] = value;
          propagateValueToHiddenRows(box, rowIndex, columnKey, value);
        }
      });
    },
    [boxId, updateBox, rowKey, columnKey, type, handleUploadImg, propagateValueToHiddenRows]
  );

  const focus = useCallback(() => {
    setIsSelected(true)
    setEventManagerEnabled(false);
    inputRef.current?.focus()
  }, [setEventManagerEnabled])

  const handleFocus = (e) => {
    if (tableIsSelected && !isSelected) {
      e.preventDefault();
      focus()
    }
  };

  useEffect(() => {
    if (box.selectedItem?.rowKey === rowKey &&
      box.selectedItem?.columnKey === columnKey &&
      box.selectedItem?.type === 'data' &&
      tableIsSelected) {
      focus()
    }
  }, [box.selectedItem?.columnKey, box.selectedItem?.rowKey, box.selectedItem?.type, columnKey, focus, rowKey, tableIsSelected]);

  const handleChange = (e) => setEditorState(e);

  const { myKeyBindingFn } = useTabHandler(editorState, setEditorState);

  const handleKeyCommand = useCallback(
    (command) => {
      if (command) {
        setEditorState(
          RichUtils.toggleInlineStyle(editorState, command.toUpperCase())
        );
      }
    },
    [editorState]
  );

  const save = useCallback((editorState) => {
    onChangeCellText(JSON.stringify(convertToRaw(editorState.getCurrentContent())));
  }, [onChangeCellText]);

  const cleanupRef = useRef(editorState);

  useEffect(() => {
    cleanupRef.current = editorState
  });

  useLayoutEffect(() => {
    return () => {
      if (tableIsSelected && type !== "image" && isSelected && cleanupRef.current) {
        if (consumeSkipBoxSaveOnUnselect(boxId)) return;
        save(cleanupRef.current);
      }
    };
  }, [tableIsSelected, type, isSelected, save, boxId, consumeSkipBoxSaveOnUnselect]);

  const { padding, minHeight } = convertPadding(tableStyle.size, tableStyle.customCellPadding, tableStyle.customCellPaddingUnit);
  const editorWrapperRef = useRef(null);
  const trRef = useRef(null);

  const ref = useRef()
  // Poignée d'un groupe de lignes fusionnées : elle prend la hauteur de la
  // cellule fusionnée. Positionnée par rapport à la première ligne (le <tr>
  // porte un transform), sa hauteur à 100 % n'en couvrait que la première,
  // souvent écrasée à quelques pixels.
  const [groupHeight, setGroupHeight] = useState(null)
  const showsGroupHandle = groupRowSpan > 1 && tableIsSelected && isLastColumn
  useLayoutEffect(() => {
    if (!showsGroupHandle || !ref.current) {
      setGroupHeight(null)
      return
    }
    const cell = ref.current
    const measure = () => setGroupHeight(cell.getBoundingClientRect().height)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(cell)
    return () => observer.disconnect()
  }, [showsGroupHandle])
  const menuAncestorIds = useMemo(() => [boxId, columnKey, rowKey], [boxId, columnKey, rowKey])

  // Sans ça, isSelected ne redescendait jamais à false en cliquant sur une
  // autre cellule/en-tête du même tableau (seul le démontage le nettoyait,
  // via le cleanup du useLayoutEffect ci-dessus) : le panneau Insertion de
  // cette cellule restait affiché indéfiniment, cumulé avec celui de la
  // cellule nouvellement sélectionnée.
  useCellClickOutside({
    ref,
    isSelected,
    enabled: type !== 'image',
    onClickOutside: () => {
      save(editorState)
      setIsSelected(false)
    },
  });

  // Collage mis en forme, ou texte seul (raccourci, pastille).
  const highlightColors = useMemo(() => mergeConfigurationHighlightColors(configuration), [configuration]);
  const styledPaste = useStyledPaste({
    editorState,
    write: setEditorState,
    anchorRef: ref,
    active: tableIsSelected && isSelected,
    colors: configuration.colors,
    highlightColors,
    defaultColor: tableStyle.customCellTextColor,
    defaultFontSize: style?.fontSize,
  });

  const handleOnMouseOver = (fromTop) => {
    document.querySelectorAll('[data-drag-row-cell].row-line-hovered').forEach(el =>
      el.classList.remove('row-line-hovered', 'is-first-row')
    )
    document.querySelectorAll(`[data-drag-row-cell="${rowKey}"]`).forEach(el => {
      el.classList.add('row-line-hovered')
      if (fromTop) el.classList.add('is-first-row')
    })
  }

  const handleOnClick = (fromTop) => {
    ref.current?.dispatchEvent(new CustomEvent('sp-row-border-click', {
      bubbles: true,
      detail: { fromTop }
    }))
  }

  const handleOnMouseLeave = () => {
    document.querySelectorAll('[data-drag-row-cell].row-line-hovered').forEach(el =>
      el.classList.remove('row-line-hovered', 'is-first-row')
    )
  }

  const setParentLineHovered = useCallback(({ isHovered, isLeftButton }) => {
    if (!isHovered) {
      document.querySelectorAll(`[data-drag-row-cell="${rowKey}"]`).forEach(el =>
        el.classList.remove('row-line-hovered', 'is-first-row')
      )
      return
    }
    document.querySelectorAll(`[data-drag-row-cell="${rowKey}"]`).forEach(el => {
      el.classList.add('row-line-hovered')
      if (isLeftButton) el.classList.add('is-first-row')
      else el.classList.remove('is-first-row')
    })
  }, [rowKey])

  return (
    // Le placement vertical se fait sur la case elle-même : le contenu, en
    // hauteur 100 % d'une case de hauteur auto, ne la remplit pas.
    <td style={{ ...style, verticalAlign: TD_VERTICAL_ALIGN[verticalAlign] ?? 'middle' }}
      rowSpan={rowSpan}
      colSpan={colSpan}
      data-drag-row-cell={rowKey}
      data-col-cell={columnKey}
      onClick={(e) => {
        // Shift+clic = sélection multi-cellules (fusion), pas d'entrée en édition.
        const handled = handleClickInTable({ rowKey, columnKey, type: 'data', rowIndex, colIndex })(e)
        if (!handled) handleFocus(e)
      }}
      ref={(el) => {
        ref.current = el;
        if (el) {
          trRef.current = el.closest('tr');
        }
      }}
      className={`${isColumnControlRowClicked || isControlRowClicked ? "table-border-clicked" : ""}
      ${isControlRowClickedToDelete || isColumnControlRowClickedToDelete ? "table-border-clicked-to-delete" : ""}
      ${isCellSelected ? "sp-cell-selected" : ""}
      `}
    >
      {fromViewer !== viewerTypes.PREVIEW && <>
        {tableIsSelected && isLastColumn &&
          <ControlRow
            controlRowsClicked={controlRowsClicked}
            isFirstElement={isFirstRow}
            isLastElement={isLastRow}
            isFromHeader={false}
            setControlRowsClicked={setControlRowsClicked}
            parentRowId={rowKey}
            parentRowType={parentRowType}
            isControlRowClicked={isControlRowClicked}
            isControlRowAfterClicked={isControlRowAfterClicked}
            setParentLineHovered={setParentLineHovered}
            zIndex={zIndex}
            boxId={boxId}
            placementIndex={placementIndex}
            rowLabel={groupRowSpan > 1 ? `${placementIndex - groupRowSpan + 1}-${placementIndex}` : undefined}
            spanHeight={groupHeight}
            parentKey={rowKey}
            moveRow={moveRow}
            rowElementRef={trRef}
            onControlRowClick={onControlRowClick}
          />}
        {isFirstRow && <div
          onMouseOver={() => handleOnMouseOver(true)}
          onMouseLeave={handleOnMouseLeave}
          onClick={() => handleOnClick(true)}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: "100%",
            height: 4,
            zIndex: 1,
            transform: "translateY(calc(-2px - 1px))"
          }} />}
        <div
          onMouseOver={() => handleOnMouseOver(false)}
          onMouseLeave={handleOnMouseLeave}
          onClick={() => handleOnClick(false)}
          style={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: "100%",
            height: 10,
            zIndex: 1,
            transform: "translateY(calc(5px + 1px))"
          }} />
      </>}
      {type === 'image' && <Upload
        name='img du chiffrage'
        showUploadList={false}
        customRequest={(e) => {
          onChangeCellText(e);
        }}
      >
        {convertContentToRawText(editorState)?.trim() !== '' ? (
          <div style={{
            position: 'relative',
            zIndex: tableIsSelected ? 1000 : 'auto',
            padding
          }}>
            {!fromPdf && fromViewer !== viewerTypes.PREVIEW && <span
              onClick={(e) => {
                e.stopPropagation();
                onChangeCellText('');
              }}
              className='--hide-from-pdf'
              style={{
                position: 'absolute',
                top: 0,
                left: 5,
                cursor: 'pointer',
                color: 'red',
                fontSize: 8,
              }}
            >
              &#10060;
            </span>}
            <img
              src={(() => { const s = convertContentToRawText(editorState); return (s.includes('localhost') || s.includes('127.0.0.1')) ? s : s.replace('http://', 'https://'); })()}
              alt='img du chiffrage'
              style={{ width: '100%' }}
            />
          </div>
        ) : (
          <div
            style={{
              margin: 'auto',
              height: '100%',
              width: style.width,
              textAlign: 'center',
              position: 'relative',
              zIndex: tableIsSelected ? 1000 : 'auto',
              overflow: 'hidden',
            }}
          >
            {isPlanningScreenshotCell ? (
              !fromPdf && fromViewer !== viewerTypes.PREVIEW && (planningCaptureFailed ? (
                <PlanningCaptureUnavailable />
              ) : (
                <Loader
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%',
                  }}
                  imageStyle={{ width: 40, height: 40 }}
                />
              ))
            ) : (
              <Tooltip
                title='Télécharger une image'
                placement='bottom'
                mouseLeaveDelay='0'
              >
                {!fromPdf && fromViewer !== viewerTypes.PREVIEW && <UploadOutlined
                  style={{ fontSize: '24px' }}
                  className='--hide-from-pdf'
                />}
              </Tooltip>
            )}
          </div>
        )}
      </Upload>}
      {type !== 'image' && <>
        <div style={{
          backgroundColor: 'transparent',
          zIndex: tableIsSelected ? 1000 : 'auto',
          border: 'none',
          color: style.color,
          padding,
          minHeight,
          height: "100%",
          fontSize: style.fontSize,
          fontFamily: style?.fontFamily,
          maxHeight: landscape ? pageWidth : maxContainerHeight({ header, footer }) - theadHeight - 10,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          width: '100%'
        }}
        >
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: verticalAlign === 'flex-start' ? 'flex-start' : verticalAlign === 'center' ? 'center' : 'flex-end',
            justifyContent: align === 'left' ? 'flex-start' : align === 'center' ? 'center' : align === 'right' ? 'flex-end' : align === 'justify' ? 'flex-start' : 'flex-start',
            minHeight: 0
          }}>
            <EditorProvider setEditorState={setEditorState} editorState={editorState} readOnly={!tableIsSelected}>
              <div
                ref={editorWrapperRef}
                style={{ width: '100%' }}
                onKeyDown={styledPaste.onKeyDown}
                onKeyUp={styledPaste.onKeyUp}
              >
                <EditorWrapper
                  boxId={`${boxId}-${columnKey}-${rowKey}-${fromViewer === viewerTypes.PREVIEW ? 'preview' : ''}`}
                  editorState={editorState}
                  box={{ id: `${boxId}-${columnKey}-${rowKey}-${fromViewer === viewerTypes.PREVIEW ? 'preview' : 'editor'}`, content: { alignment: align } }}
                  textStyle={{ positions: { horizontal: align } }}
                >
                  <Editor
                    stripPastedStyles={true}
                    handlePastedText={styledPaste.handlePastedText}
                    handleBeforeInput={(chars, newEditorState) => handleBeforeInput(
                      {
                        chars,
                        editorState: newEditorState,
                        setEditorState
                      }
                    )}
                    editorState={editorState}
                    onChange={handleChange}
                    ref={inputRef}
                    textAlignment={align === 'justify' ? 'left' : align}
                    spellCheck={true}
                    readOnly={!tableIsSelected}
                    customStyleMap={styleMap}
                    keyBindingFn={myKeyBindingFn}
                    handleKeyCommand={handleKeyCommand}
                    onBlur={() => {
                      save(editorState);
                      updateBox(boxId, (box) => {
                        box.selectedItem = undefined
                      }
                      );
                    }}
                  />
                </EditorWrapper>
              </div>
            </EditorProvider>
          </div>
        </div>
        {tableIsSelected &&
          ReactDOM.createPortal(
            <ContextualMenu
              editorState={editorState}
              setEditorState={setEditorState}
              menuAncestorType={MENU_ANCESTORS.BOX.cell}
              menuAncestorIds={menuAncestorIds}
              defaultSelection={defaultSelection}
              setDefaultSelection={setDefaultSelection}
            />,
            document.getElementById('SelectionVariablePortal')
          )}
      </>}
      {styledPaste.pasteChoice}
      {tableIsSelected && isSelected &&
        ReactDOM.createPortal(
          <FloatyTextStyling
            editorState={editorState}
            setEditorState={setEditorState}
            defaultFontSize={style?.fontSize}
            defaultFontFamily={style?.fontFamily}
            defaultColor={tableStyle.customCellTextColor}
            colors={configuration.colors}
          />,
          document.getElementById('SelectionVariablePortal')
        )}
      {(!isLastColumn || drawMode) && fromViewer !== viewerTypes.PREVIEW && <ColumnResizer
        boxId={boxId}
        rotation={rotation}
        setColumnSizeVars={setColumnSizeVars}
        columnKey={columnKey}
        nextColKey={nextColKey}
        computeMaxColumnWidth={computeMaxColumnWidth}
        resizeEnabled={isLastColumn ? (drawMode && tableIsSelected) : (!drawMode || tableIsSelected)}
      />}
    </td>
  )
}

export default React.memo(Cell)
